/**
 * **Le jour de l'ORGANISME, à l'écran** — tout module confondu (décisions PO D43
 * et D60 du 2026-09-23 pour le Matériel, D116 pour le reste du produit).
 *
 * C'est la moitié ÉCRAN de l'outil serveur `jour_de_l_organisme`
 * (`backend/src/infra/datatable_filters/`) : le jour que le serveur sert dans
 * `GET /api/tenant/config` → `today`, celui qui décide de ses refus « date dans
 * le futur », de la date d'un devis, de la clôture d'une NC, de l'expiration d'un
 * contrat-cadre. Rangé ici, hors de tout module métier, pour qu'un écran le lise
 * sans dépendre du Matériel : c'est son SEUL chemin, les écrans du Matériel
 * compris.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * CE QUE CE MODULE RÉPARE
 * ═════════════════════════════════════════════════════════════════════════════
 * Le serveur refuse un passage, un relevé, une levée de réserve ou une
 * correction datés « dans le futur » au jour de l'ORGANISME
 * (`tenant_config.timezone`, D30, D46). Les dialogues pré-remplissaient, eux, au
 * jour du NAVIGATEUR (`todayDateInput()`, `new Date()`) et bornaient au jour le
 * plus avancé du globe (`jourLePlusAvance.ts`) : un poste à Paris qui saisit pour
 * un organisme à La Réunion — ou l'inverse — voyait « aujourd'hui » pré-rempli
 * au mauvais jour, et le lendemain de l'organisme restait offert pour un refus
 * garanti.
 *
 * Le serveur sert désormais ce jour (`GET /api/tenant/config` → `today`) : il
 * pré-remplit ET borne. Sans lui (serveur ancien, config pas encore arrivée),
 * chaque usage retombe sur son comportement d'AVANT, au caractère près : le jour
 * du navigateur pour le pré-remplissage, le jour de UTC+14 pour la borne — le
 * filet qui ne grise jamais un jour que le serveur accepte.
 */
import { useTenantConfig } from '@/hooks/useTenantConfig'
import { fromISODate, todayDateInput } from '@/lib/date-utils'
import { leJourLePlusAvanceDuGlobe } from '@/lib/jourLePlusAvance'

const JOUR_ISO = /^\d{4}-\d{2}-\d{2}$/

export interface JourDeLOrganisme {
    /** Le jour à PRÉ-REMPLIR (`YYYY-MM-DD`). */
    aujourdhui: string
    /** Le même, en minuit local — pour le bouton « Aujourd'hui » du calendrier. */
    aujourdhuiDate: Date
    /** La borne HAUTE d'un sélecteur de date « déjà vécue » (minuit local). */
    borneHaute: Date
    /** Le jour vient-il du serveur ? Faux = repli sur le navigateur. */
    servi: boolean
}

export function useJourDeLOrganisme(): JourDeLOrganisme {
    const { today } = useTenantConfig()
    const servi = typeof today === 'string' && JOUR_ISO.test(today) && fromISODate(today) !== null
    if (servi) {
        const jour = fromISODate(today)!
        return { aujourdhui: today, aujourdhuiDate: jour, borneHaute: jour, servi: true }
    }
    const navigateur = todayDateInput()
    return {
        aujourdhui: navigateur,
        aujourdhuiDate: fromISODate(navigateur)!,
        borneHaute: leJourLePlusAvanceDuGlobe(),
        servi: false,
    }
}
